<template>
  <div>
    <div class="text-subtitle-1 mb-1">QuickBooks</div>
    <div class="text-body-2 mb-3">
      Posts to: {{ company.companyName || 'No QuickBooks company' }}
    </div>
    <v-alert v-if="company.explanation" type="info" outlined dense>
      {{ company.explanation }}
    </v-alert>
    <v-alert v-if="loadError" type="error" outlined dense>
      {{ loadError }}
    </v-alert>
    <v-row>
      <v-col cols="12" sm="6" md="4">
        <v-autocomplete
          :value="customerId"
          :items="customerOptions"
          item-text="displayName"
          item-value="id"
          label="QuickBooks Customer"
          outlined
          clearable
          :loading="loading"
          :disabled="!company.connected"
          @change="selectCustomer"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-autocomplete
          :value="itemId"
          :items="itemOptions"
          item-text="name"
          item-value="id"
          label="QuickBooks Timesheet Item"
          outlined
          clearable
          :loading="loading"
          :disabled="!company.connected"
          @change="selectItem"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-switch
          :input-value="postAutomatically"
          label="Post to QuickBooks automatically"
          hint="Posts this client's invoices to QuickBooks every day."
          persistent-hint
          :disabled="!company.connected"
          @change="selectPostAutomatically"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { clientQuickBooksCompany, withStoredOption } from './quickBooksConnectionHelpers'

export default {
  props: {
    businessEntityId: { type: Number, default: null },
    customerId: { type: String, default: null },
    itemId: { type: String, default: null },
    postAutomatically: { type: Boolean, default: false }
  },
  data: () => ({
    customers: [],
    items: [],
    loading: false,
    loadError: null
  }),
  computed: {
    ...mapFields('quickBooksConnections', ['connections', 'configurationError']),
    company () {
      return clientQuickBooksCompany({
        connections: this.connections,
        businessEntityId: this.businessEntityId,
        configurationError: this.configurationError
      })
    },
    listsLoaded () {
      return !this.loading && !this.loadError && this.company.connected
    },
    customerOptions () {
      return this.listsLoaded ? withStoredOption(this.customers, this.customerId, 'displayName') : this.customers
    },
    itemOptions () {
      return this.listsLoaded ? withStoredOption(this.items, this.itemId, 'name') : this.items
    },
    connectedBusinessEntityId () {
      return this.company.connected ? this.businessEntityId : null
    }
  },
  watch: {
    connectedBusinessEntityId: {
      handler (businessEntityId) {
        this.loadLists(businessEntityId)
      },
      immediate: true
    }
  },
  created () {
    this.getConnections()
  },
  methods: {
    ...mapActions('quickBooksConnections', ['getConnections', 'listCustomers', 'listItems']),
    loadLists (businessEntityId) {
      this.customers = []
      this.items = []
      this.loadError = null
      if (!businessEntityId) {
        return Promise.resolve()
      }

      this.loading = true
      return Promise.all([
        this.listCustomers({ businessEntityId }),
        this.listItems({ businessEntityId })
      ])
        .then(([customers, items]) => {
          this.customers = customers
          this.items = items
        })
        .catch(error => {
          this.loadError = error.message
        })
        .finally(() => {
          this.loading = false
        })
    },
    selectCustomer (value) {
      this.$emit('update:customerId', value || '')
    },
    selectItem (value) {
      this.$emit('update:itemId', value || '')
    },
    selectPostAutomatically (value) {
      this.$emit('update:postAutomatically', !!value)
    }
  }
}
</script>
